// /markt/trending — CoinGecko Trending Coins der letzten 24h.
// Phase 2 (2026-05-15): pro Coin „Frag EVE"-Button mit echter Web-Recherche.

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { selectFullData, useMarketStore } from '@/stores/market-store';
import { useEvePageContextStore } from '@/stores/eve-page-context-store';
import { useEveDrawerStore } from '@/stores/eve-drawer-store';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';
import { searchWeb } from '@/lib/serp-api';
import { buildTrendingPrompt, trendingSearchQuery } from '@/lib/trending-research';
import { TrendingCard } from '@/components/markets/trending-card';

export function TrendingPage() {
  const fullData = useMarketStore(selectFullData);
  const accessToken = useAuthStore(selectAccessToken);
  const setPageContext = useEvePageContextStore((s) => s.setPageContext);
  const clearPageContext = useEvePageContextStore((s) => s.clearPageContext);
  const askEve = useEveDrawerStore((s) => s.askEve);
  const openDrawer = useEveDrawerStore((s) => s.open);
  const [researchingSymbol, setResearchingSymbol] = useState<string | null>(null);

  useEffect(() => {
    const v3Flat = (fullData?.trending as unknown) as
      | Array<{ symbol?: string; name?: string; marketCapRank?: number | null }>
      | undefined;
    const coins = Array.isArray(v3Flat)
      ? v3Flat.slice(0, 15).map((c) => ({
          symbol: c.symbol,
          name: c.name,
          mcapRank: c.marketCapRank,
        }))
      : (fullData?.trending?.coins ?? []).slice(0, 15).map((c) => ({
          symbol: c.item?.symbol,
          name: c.item?.name,
          mcapRank: c.item?.market_cap_rank,
        }));
    setPageContext({
      pageKey: '/markt/trending',
      title: 'Trending Coins',
      summary: `CoinGecko trending Coins der letzten 24h. ${coins.length} Coins geladen.`,
      data: { coins },
    });
    return () => clearPageContext();
  }, [fullData?.trending, setPageContext, clearPageContext]);

  const handleAskEve = async (coin: { symbol: string; name: string }) => {
    if (researchingSymbol) return;
    setResearchingSymbol(coin.symbol);
    openDrawer();

    // Echte Recherche: vorab Web-Suche zum Coin holen (/serp), Ergebnisse in den
    // Prompt packen — EVE fasst NUR diesen Coin + den Trending-Grund zusammen.
    // `noLiveData` blendet den Gesamtmarkt-Snapshot aus → kein Makro-Dump.
    try {
      const res = await searchWeb(trendingSearchQuery(coin), accessToken ?? undefined);
      const prompt = buildTrendingPrompt(coin, res);
      askEve(prompt, {
        displayText: `Recherche: ${coin.symbol} — warum trending?`,
        noLiveData: true,
      });
    } finally {
      setResearchingSymbol(null);
    }
  };

  return (
    <div className="relative h-full overflow-y-auto">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -right-32 top-12 size-96 rounded-full bg-rose-500/[0.06] blur-3xl" />
        <div className="absolute left-12 top-1/3 size-80 rounded-full bg-amber-500/[0.04] blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-5xl space-y-6 px-6 py-8">
        <motion.header
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          className="space-y-2 border-b border-white/5 pb-4"
        >
          <div className="flex items-baseline gap-3">
            <h1 className="bg-gradient-to-r from-rose-200 via-amber-100 to-amber-300 bg-clip-text text-3xl font-bold tracking-tight text-transparent">
              Trending
            </h1>
            <span className="text-[11px] uppercase tracking-widest text-neutral-600">
              CoinGecko · Retail-Aufmerksamkeit
            </span>
          </div>
          <p className="max-w-2xl text-sm leading-relaxed text-neutral-400">
            Was Retail in den letzten 24h gesucht hat. Hot Coins sind oft
            Pump-Kandidaten — Edge ist nicht das Mitlaufen, sondern das Erkennen
            wann die Aufmerksamkeit kippt. Klick „EVE" für echte Web-Recherche
            pro Coin.
          </p>
        </motion.header>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28, delay: 0.05 }}
        >
          <TrendingCard
            fullData={fullData}
            limit={15}
            onAskEve={handleAskEve}
            researchingSymbol={researchingSymbol}
          />
        </motion.div>
      </div>
    </div>
  );
}
